import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  FiUsers,
  FiClipboard,
  FiBriefcase,
  FiCopy,
  FiCheck,
  FiLink,
} from 'react-icons/fi';
import { toast } from 'react-toastify';

import { createMentorCode } from '../../../services/mentorService';

/**
 * Footer band that bundles together everything the legacy mentor dashboard
 * used to surface at the top: the mentor code, registration link, the
 * "create a custom code" form, and quick-action tiles for the other mentor
 * pages. We push these to the bottom because they're reference/navigation
 * elements, not the analytics the mentor opens the page to read.
 */
const ROW_LINKS = [
  {
    to: '/mentor/mentees',
    icon: FiUsers,
    title: 'All mentees',
    sub: 'Detailed list of every mentee',
    bg: 'bg-primary-100',
    color: 'text-primary-600',
  },
  {
    to: '/mentor/tickets',
    icon: FiClipboard,
    title: 'Mentee tickets',
    sub: 'View and respond to support tickets',
    bg: 'bg-yellow-100',
    color: 'text-yellow-600',
  },
  {
    to: '/mentor/job-assignments',
    icon: FiBriefcase,
    title: 'Job assignments',
    sub: 'Push roles into mentees\u2019 job lists',
    bg: 'bg-blue-100',
    color: 'text-blue-600',
  },
];

const MentorInviteFooter = ({ invite, onCodeUpdated }) => {
  const [code, setCode] = useState(invite?.code || '');
  const [customCode, setCustomCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState({ code: false, link: false });
  const copyTimers = useRef({ code: null, link: null });

  useEffect(() => {
    setCode(invite?.code || '');
  }, [invite?.code]);

  useEffect(() => {
    return () => {
      Object.values(copyTimers.current).forEach((t) => t && clearTimeout(t));
    };
  }, []);

  const registrationLink = code
    ? `${window.location.origin}/register?mentor_code=${code}`
    : '';

  const copy = (key, text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => {
        if (copyTimers.current[key]) clearTimeout(copyTimers.current[key]);
        setCopied((p) => ({ ...p, [key]: true }));
        toast.success('Copied to clipboard');
        copyTimers.current[key] = setTimeout(
          () => setCopied((p) => ({ ...p, [key]: false })),
          2000
        );
      },
      () => toast.error('Failed to copy to clipboard')
    );
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setCreating(true);
      const response = await createMentorCode(customCode || null);
      const newCode = response.data?.code;
      if (newCode) {
        setCode(newCode);
        setCustomCode('');
        toast.success('Mentor code updated');
        onCodeUpdated?.(newCode);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create mentor code');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="bg-white shadow-sm rounded-xl overflow-hidden p-5 border border-gray-100">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-7 space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Invite & quick actions</h3>
            <p className="text-xs text-gray-500">
              Share your mentor code or jump into the most-used mentor pages.
            </p>
          </div>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
              Mentor code
            </p>
            <div className="flex items-center">
              <div className="flex-1 bg-primary-50 text-primary-700 font-mono text-sm px-3 py-2 rounded-l-md border border-primary-200 truncate">
                {code || '—'}
              </div>
              <button
                type="button"
                onClick={() => copy('code', code)}
                disabled={!code}
                className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-2 rounded-r-md transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                aria-label={copied.code ? 'Code copied' : 'Copy mentor code'}
              >
                {copied.code
                  ? <FiCheck className="h-4 w-4" />
                  : <FiCopy className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
              Registration link
            </p>
            <div className="flex items-center">
              <div className="flex-1 bg-gray-50 text-gray-600 text-xs px-3 py-2 rounded-l-md border border-gray-200 truncate">
                {registrationLink || '—'}
              </div>
              <button
                type="button"
                onClick={() => copy('link', registrationLink)}
                disabled={!registrationLink}
                className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-r-md transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                aria-label={copied.link ? 'Link copied' : 'Copy registration link'}
              >
                {copied.link
                  ? <FiCheck className="h-4 w-4" />
                  : <FiLink className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <form onSubmit={handleCreate} className="flex items-center pt-1">
            <input
              type="text"
              value={customCode}
              onChange={(e) => setCustomCode(e.target.value)}
              placeholder="Create custom code (optional)"
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button
              type="submit"
              disabled={creating}
              className="bg-primary-600 hover:bg-primary-700 text-white text-sm px-4 py-2 rounded-r-md transition-colors disabled:bg-primary-400"
            >
              {creating ? 'Creating…' : 'Create'}
            </button>
          </form>

          {invite && (
            <p className="text-[11px] text-gray-500 pt-1">
              New mentees who use this code start as{' '}
              <span className="font-semibold text-gray-700">{invite.menteeStatus}</span>
              {' '}with{' '}
              <span className="font-semibold text-gray-700">
                {invite.giftCredits} credits
              </span>
              .
            </p>
          )}
        </div>

        <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-3">
          {ROW_LINKS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="flex items-center gap-3 p-3 border border-gray-100 hover:border-gray-200 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <div className={`p-2 rounded-full ${item.bg}`}>
                <item.icon className={`h-4 w-4 ${item.color}`} />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900 truncate">{item.title}</div>
                <div className="text-[11px] text-gray-500 truncate">{item.sub}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MentorInviteFooter;
